/**
 * refreshCoordinator — cross-tab single-flight for the silent token refresh
 * (SESSION PR-C2b-A).
 *
 * The axios interceptor already does IN-TAB single-flight (one refresh at a time
 * per tab, via its `isRefreshing` flag + queue). This module adds CROSS-TAB
 * coordination so that, when several tabs hit a 401 at once, they do not all
 * rotate the refresh token simultaneously.
 *
 * Mechanism (safe-by-default — coordination can only REDUCE redundant refreshes,
 * never deadlock or skip a refresh that is genuinely needed):
 *  - A short-lived localStorage marker (`amuster_auth_refresh`) records that SOME
 *    tab is refreshing, as a timestamp. The marker is treated as stale after
 *    LOCK_TTL_MS, so a crashed/closed tab can never wedge the others.
 *  - A BroadcastChannel (`amuster-auth-refresh`) wakes waiting tabs the instant a
 *    peer finishes, with whether it succeeded — so a waiting tab can retry its
 *    original request against the now-fresh HttpOnly cookies WITHOUT issuing its
 *    own refresh (on success) or fail fast (on peer failure).
 *  - If a peer refresh does not complete within WAIT_TIMEOUT_MS, the waiting tab
 *    falls back to doing its own refresh.
 *
 * NOTE: backend refresh-token rotation is idempotent within a grace window
 * (SESSION PR-C2a), so even a rare simultaneous double-refresh is safe — this is
 * an optimization, not a correctness dependency.
 *
 * SECURITY: NO token/cookie/CSRF/Authorization value is ever stored or
 * broadcast. The localStorage marker holds only a timestamp; the channel carries
 * only a coarse 'start'/'done' signal (+ an `ok` boolean).
 */

const LOCK_KEY = 'amuster_auth_refresh';
const CHANNEL_NAME = 'amuster-auth-refresh';
/** A marker older than this (ms) is treated as stale and ignored. */
const LOCK_TTL_MS = 8_000;
/** Max time (ms) a tab waits for a peer's in-flight refresh before doing its own. */
const WAIT_TIMEOUT_MS = 8_000;
/** Poll cadence (ms) for the localStorage fallback (when BroadcastChannel is absent). */
const POLL_INTERVAL_MS = 250;

type Signal = { type: 'start' } | { type: 'done'; ok: boolean };
type PeerResult = 'ok' | 'failed' | 'timeout';

function nowMs(): number {
  return Date.now();
}

function readLockTs(): number {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    if (!raw) return 0;
    const ts = Number.parseInt(raw, 10);
    return Number.isFinite(ts) ? ts : 0;
  } catch {
    return 0;
  }
}

/** True if some OTHER tab announced a refresh that is recent enough to wait for. */
function peerRefreshActive(): boolean {
  const ts = readLockTs();
  return ts > 0 && nowMs() - ts < LOCK_TTL_MS;
}

function setLock(): void {
  try {
    localStorage.setItem(LOCK_KEY, String(nowMs()));
  } catch {
    /* storage unavailable — coordination simply degrades to per-tab refresh */
  }
}

function clearLock(): void {
  try {
    localStorage.removeItem(LOCK_KEY);
  } catch {
    /* ignore */
  }
}

function openChannel(): BroadcastChannel | null {
  try {
    return typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;
  } catch {
    return null;
  }
}

/**
 * Wait for a peer tab to finish refreshing, or until timeout.
 * Resolves 'ok' (peer rotated successfully — our cookies are now fresh),
 * 'failed' (peer's refresh failed), or 'timeout' (no peer signal in time).
 */
function waitForPeerDone(timeoutMs: number): Promise<PeerResult> {
  return new Promise((resolve) => {
    const channel = openChannel();
    let settled = false;
    let poll: ReturnType<typeof setInterval> | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const cleanup = (): void => {
      if (poll) clearInterval(poll);
      if (timer) clearTimeout(timer);
      try {
        channel?.close();
      } catch {
        /* ignore */
      }
    };
    const finish = (result: PeerResult): void => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };

    if (channel) {
      channel.onmessage = (e: MessageEvent<Signal>): void => {
        if (e.data?.type === 'done') finish(e.data.ok ? 'ok' : 'failed');
      };
    }
    // Fallback for when BroadcastChannel is unavailable: resolve 'ok' only when
    // the marker is EXPLICITLY cleared (peer finished). A marker that merely goes
    // stale (crashed/closed peer) is left to the timeout below, so this tab falls
    // back to its OWN refresh rather than assuming a success that never happened.
    poll = setInterval(() => {
      if (readLockTs() === 0) finish('ok');
    }, POLL_INTERVAL_MS);
    timer = setTimeout(() => finish('timeout'), timeoutMs);
  });
}

/**
 * Run `doRefresh` under cross-tab single-flight.
 *
 * - If a peer tab is already refreshing: wait for it. On peer success, resolve
 *   WITHOUT refreshing (the HttpOnly cookies are shared and now fresh). On peer
 *   failure, throw (the caller treats it as a refresh failure). On timeout, fall
 *   through and do our own refresh.
 * - Otherwise: claim the marker, run `doRefresh`, then always release the marker
 *   and broadcast the outcome.
 *
 * Re-throws whatever `doRefresh` throws, so the interceptor's existing
 * success/failure handling (queue drain, CSRF recovery, session-expired signal)
 * is unchanged.
 */
export async function coordinateRefresh(doRefresh: () => Promise<void>): Promise<void> {
  if (typeof window === 'undefined') {
    await doRefresh();
    return;
  }

  if (peerRefreshActive()) {
    const result = await waitForPeerDone(WAIT_TIMEOUT_MS);
    if (result === 'ok') return; // peer rotated; our cookies are fresh
    if (result === 'failed') throw new Error('peer_refresh_failed');
    // 'timeout' → fall through to our own refresh
  }

  setLock();
  const channel = openChannel();
  let ok = false;
  try {
    channel?.postMessage({ type: 'start' } as Signal);
    await doRefresh();
    ok = true;
  } finally {
    clearLock();
    try {
      channel?.postMessage({ type: 'done', ok } as Signal);
    } catch {
      /* ignore */
    }
    try {
      channel?.close();
    } catch {
      /* ignore */
    }
  }
}

/** Test-only: reset the cross-tab marker. @internal */
export function __resetRefreshCoordinatorForTests(): void {
  clearLock();
}
